import React, { useEffect, useState } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StockReportItem, ReportFilter } from '../../model/report';
import { fetchInventoryReport } from '../../api/report';
import { exportExcel } from '../../utils/util';
import { useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';

interface Props {
    filter?: ReportFilter;
}

const ReportInventory: React.FC<Props> = ({ filter }) => {
    const pagination = useTablePagination();
    const { ref: tableRef, y: scrollY } = useMeasuredRowScrollY(420);
    const [stockItems, setStockItems] = useState<StockReportItem[]>([]);
    const [totalStockCount, setTotalStockCount] = useState(0);
    const [totalStockCostValue, setTotalStockCostValue] = useState(0);
    const [lowStockCount, setLowStockCount] = useState(0);
    // Products with no row in `stock` at all: imported into the catalogue
    // but never received. They used to be counted as "ใกล้หมด", which is
    // how this screen came to report 2,584 of 2,610 products as needing
    // attention when ~186 actually did.
    const [neverStockedCount, setNeverStockedCount] = useState(0);
    const [outOfStockCount, setOutOfStockCount] = useState(0);
    const [filterLowStockOnly, setFilterLowStockOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchInventoryReport();
                if (res) {
                    setStockItems(res.stockItems || []);
                    setTotalStockCount(res.totalStockCount || 0);
                    setTotalStockCostValue(res.totalStockCostValue || 0);
                    setLowStockCount(res.lowStockCount || 0);
                    setNeverStockedCount(res.neverStockedCount || 0);
                    setOutOfStockCount(res.outOfStockCount || 0);
                }
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        };
        load();
    }, [filter]);

    const displayedItems = stockItems.filter(item => {
        const matchesSearch = item.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.product_name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesLowStock = filterLowStockOnly ? item.is_low_stock : true;
        return matchesSearch && matchesLowStock;
    });

    const stockColumns: ColumnsType<StockReportItem> = [
        { title: 'รหัสสินค้า', dataIndex: 'product_code', key: 'product_code', width: 120 },
        {
            title: 'ชื่อสินค้า',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (val: string) => <span style={{ fontWeight: 600 }}>{val}</span>
        },
        { title: 'หมวดหมู่', dataIndex: 'category_name', key: 'category_name' },
        {
            title: 'คงเหลือ',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (val: number, record: StockReportItem) => (
                <span style={{ fontWeight: 700, color: record.is_low_stock ? '#EF4444' : 'inherit' }}>
                    {val} {record.unit_name}
                </span>
            )
        },
        {
            title: 'จุดสั่งซื้อ (Min)',
            dataIndex: 'min_quantity',
            key: 'min_quantity',
            // NULL where the shop has set nothing. This used to print a
            // COALESCEd "10" -- an order point nobody had chosen, shown as
            // if they had.
            render: (val: number | null, record: StockReportItem) =>
                val === null || val === undefined
                    ? <span style={{ color: '#94A3B8' }}>ไม่ได้ตั้ง</span>
                    : `${val} ${record.unit_name}`
        },
        {
            title: 'สถานะสต๊อก',
            dataIndex: 'is_low_stock',
            key: 'is_low_stock',
            // Four states, not two. A binary low/normal column reported a
            // product with no stock row at all as "✅ ปกติ" -- 0 คงเหลือ
            // with a green tick -- which is how this was spotted.
            render: (_isLow: boolean, record: StockReportItem) => {
                if (record.is_never_stocked) {
                    return <span className="report-stock-status-tag never">📥 ยังไม่ได้ลงสต๊อก</span>;
                }
                if (record.is_out_of_stock) {
                    return <span className="report-stock-status-tag out">🔴 สินค้าหมด</span>;
                }
                if (record.is_low_stock) {
                    return <span className="report-stock-status-tag low">⚠️ สต๊อกใกล้หมด</span>;
                }
                return <span className="report-stock-status-tag normal">✅ ปกติ</span>;
            }
        },
        {
            title: 'ราคาทุนต่อหน่วย',
            dataIndex: 'cost_price',
            key: 'cost_price',
            render: (val: number) => `฿${val.toLocaleString()}`
        },
        {
            title: 'มูลค่าทุนรวม',
            dataIndex: 'total_cost_value',
            key: 'total_cost_value',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿{val.toLocaleString()}</span>
        }
    ];

    const handleExportExcel = () => {
        const exportData = displayedItems.map(item => ({
            'รหัสสินค้า': item.product_code,
            'ชื่อสินค้า': item.product_name,
            'หมวดหมู่': item.category_name,
            'จำนวนคงเหลือ': item.quantity,
            'หน่วย': item.unit_name,
            'จุดสั่งซื้อขั้นต่ำ': item.min_quantity,
            'สถานะ': item.is_low_stock ? 'สต๊อกใกล้หมด' : 'ปกติ',
            'ราคาทุนต่อหน่วย': item.cost_price,
            'ราคาขาย': item.sale_price,
            'มูลค่าทุนรวม': item.total_cost_value
        }));
        exportExcel('รายงานสินค้าคงเหลือและสต๊อก', exportData);
    };

    return (
        <div>
            {/* Inventory KPI Cards */}
            <div className="report-kpi-grid">
                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-items">
                        <FontAwesomeIcon icon="boxes-stacked" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">จำนวนสินค้าในคลังทั้งหมด</span>
                        <span className="kpi-value">{totalStockCount.toLocaleString()} ชิ้น</span>
                        <span className="kpi-subtext">รายการสินค้าทั้งหมด {stockItems.length} รายการ</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-profit">
                        <FontAwesomeIcon icon="coins" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">มูลค่าต้นทุนสต๊อกรวม</span>
                        <span className="kpi-value" style={{ color: '#10B981' }}>฿{totalStockCostValue.toLocaleString()}</span>
                        <span className="kpi-subtext">ประเมินตามราคาทุน</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-items" style={{ background: '#F1F5F9', color: '#475569', borderColor: '#CBD5E1' }}>
                        <FontAwesomeIcon icon="inbox" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ยังไม่ได้ลงสต๊อก</span>
                        <span className="kpi-value" data-testid="never-stocked-count" style={{ color: '#475569' }}>{neverStockedCount.toLocaleString()} รายการ</span>
                        <span className="kpi-subtext">นำเข้ารายการแล้ว แต่ยังไม่เคยรับเข้า</span>
                    </div>
                </div>

                <div
                    className="kpi-card"
                    style={{ cursor: 'pointer', borderColor: filterLowStockOnly ? '#EF4444' : undefined }}
                    onClick={() => setFilterLowStockOnly(!filterLowStockOnly)}
                    title="คลิกเพื่อกรองดูเฉพาะสินค้าสต๊อกใกล้หมด"
                >
                    <div className="kpi-icon-box kpi-sales" style={{ background: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' }}>
                        <FontAwesomeIcon icon="triangle-exclamation" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">หมด / ใกล้หมด</span>
                        <span className="kpi-value" style={{ color: '#DC2626' }} data-testid="out-and-low-count">{outOfStockCount + lowStockCount} รายการ</span>
                        <span className="kpi-subtext">{filterLowStockOnly ? 'แสดงเฉพาะที่ต้องสั่งเพิ่ม (คลิกเพื่อดูทั้งหมด)' : `หมด ${outOfStockCount} · ใกล้หมด ${lowStockCount}`}</span>
                    </div>
                </div>
            </div>

            {/* Stock Items Table */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="warehouse" style={{ color: 'var(--primary, #FF7A59)' }} />
                        <span>รายการสินค้าคงเหลือ ({displayedItems.length} รายการ)</span>
                    </span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            type="text"
                            className="report-search-input"
                            placeholder="ค้นหารหัสหรือชื่อสินค้า..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        <button
                            type="button"
                            className="btn-report-action btn-excel"
                            onClick={handleExportExcel}
                        >
                            <FontAwesomeIcon icon="file-excel" /> ส่งออก Excel
                        </button>
                    </div>
                </div>

                <div ref={tableRef}>
                <Table
                    columns={stockColumns}
                    dataSource={displayedItems}
                    rowKey="id"
                    pagination={pagination}
                    scroll={{ y: scrollY }}
                    loading={loading}
                />
                </div>
            </div>
        </div>
    );
};

export default ReportInventory;
